import { Box, Menu, Tooltip } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { CoachEventDetailsActionEnum, EventDetails } from '../../../backendsdk';
import { TrackedLink as Link, TrackedMenuItem as MenuItem } from '../../../components/TrackedComponents';
import useApi from '../../../hooks/api';
import useDrivers from '../../../hooks/drivers';
import QuickCoachingModal from '../Accident/AccidentV1/Modal/QuickCoachingModal';
import { STATUS } from '../Drivers/DriverModal';
import EventCoachingComponent from './EventCoaching';

interface EventShareMenuComponentProps {
    details?: EventDetails;
    anchorElShare: null | HTMLElement;
    setAnchorElShare: CallableFunction;
    setAlert: CallableFunction;
    showCoaching?: boolean;
    children?: React.ReactNode;
}

const EventShareMenuComponent: React.FC<EventShareMenuComponentProps> = (props: EventShareMenuComponentProps) => {
    const [isQuickCoachingOpen, setIsQuickCoachingOpen] = useState<boolean>(false);
    const [isSaved, setIsSaved] = useState<boolean>(props.details?.coach.event != undefined);
    const [coachingModal, setCoachingModal] = useState<boolean>(false);
    const { t } = useTranslation();
    const isShareOpen = Boolean(props.anchorElShare);
    const { details } = props;
    const { api } = useApi();
    const { drivers } = useDrivers();

    const driver = drivers.find((driver) => driver.token === details?.trip?.driver);

    const handleShareMenuClose = () => {
        props.setAnchorElShare(null);
    };

    const handleEventAction = (status: STATUS, comment?: string, is_group?: boolean) => {
        if (status === STATUS.SAVED) {
            setCoachingModal(false);
        }
        // add alert on remove succeed
        api.apiV0EventEventIdCoachPost({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            eventId: props.details!.event_id,
            coachEventDetails: {
                action: status === STATUS.SAVED ? CoachEventDetailsActionEnum.Add : CoachEventDetailsActionEnum.Remove,
                comment,
                is_group,
            },
        })
            .then((res) => {
                if (status === STATUS.SAVED) {
                    const link = (
                        <Link
                            id="view-coaching-session-link"
                            component={RouterLink}
                            to={`/coaching/${res.data.session_id}`}
                        >
                            {t('content.safety.view_session')}
                        </Link>
                    );
                    setIsSaved(true);
                    props.setAlert({
                        message: t('content.safety.save_success'),
                        link,
                        type: 'success',
                        duration: 6000,
                    });
                } else {
                    setIsSaved(false);
                    props.setAlert({
                        message: t('content.events.coach.remove_success'),
                        type: 'success',
                        duration: 6000,
                    });
                }
            })
            .catch((err) => {
                if (status === STATUS.DISMISSED && err.response?.data?.detail === 'event in completed session') {
                    return props.setAlert({
                        message: t('content.safety.dismiss_fail_completed'),
                        type: 'error',
                        duration: 6000,
                    });
                } else {
                    const message =
                        status === STATUS.SAVED ? t('content.safety.save_fail') : t('content.safety.dismiss_fail');
                    props.setAlert({ message, type: 'error', duration: 6000 });
                }
            });
    };

    const disableQuickCoaching = isSaved || !details?.trip?.driver;

    let quickCoachingMenuItem = (
        <Box>
            <MenuItem
                id="quick-coaching-menu-item"
                onClick={() => {
                    setIsQuickCoachingOpen(true);
                    handleShareMenuClose();
                }}
                disabled={disableQuickCoaching}
            >
                {t('content.accidents.modal_header.share.send_coaching')}
            </MenuItem>
        </Box>
    );
    if (disableQuickCoaching) {
        quickCoachingMenuItem = (
            <Tooltip title={isSaved ? t('content.events.coach.already_saved') : t('content.events.coach.no_driver')}>
                <Box>{quickCoachingMenuItem}</Box>
            </Tooltip>
        );
    }

    return (
        <>
            <Menu
                id="share-menu"
                anchorEl={props.anchorElShare}
                open={isShareOpen}
                onClose={handleShareMenuClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            >
                {props.children}
                {isSaved ? (
                    <MenuItem
                        id="btn-remove-from-coaching"
                        onClick={() => {
                            handleEventAction(STATUS.DISMISSED);
                            handleShareMenuClose();
                        }}
                        disabled={!details}
                    >
                        {t('content.events.coach.remove')}
                    </MenuItem>
                ) : (
                    <MenuItem
                        id="save-for-coaching-btn"
                        onClick={() => {
                            setCoachingModal(true);
                            handleShareMenuClose();
                        }}
                        disabled={!details}
                    >
                        {t('content.safety.save_event')}
                    </MenuItem>
                )}
                {quickCoachingMenuItem}
            </Menu>
            {props.showCoaching && details ? (
                <EventCoachingComponent
                    {...props}
                    coachingModal={coachingModal}
                    setCoachingModal={setCoachingModal}
                    handleEventAction={handleEventAction}
                    setIsSaved={setIsSaved}
                    details={details}
                    driver={driver}
                />
            ) : null}
            {isQuickCoachingOpen && details ? (
                <QuickCoachingModal
                    isOpen={isQuickCoachingOpen}
                    licensePlate={details.device.license_plate}
                    driverToken={props.details?.trip?.driver}
                    onSend={() => {
                        setIsQuickCoachingOpen(false);
                        setIsSaved(true);
                    }}
                    onClose={() => setIsQuickCoachingOpen(false)}
                    eventId={details.event_id}
                    setAlert={props.setAlert}
                    dialog
                />
            ) : null}
        </>
    );
};

export default EventShareMenuComponent;
